import { useMemo } from "react";
import type { QueueSummary, QueueTreeResponse } from "../../../experience-shell/queues/types";
import { buildQueueNameResolver, type QueueNameResolver } from "../workflowRecipients";
import { displayNameFor, flattenWorkable } from "./recipients";

/** Per-tree lookups the compose surface reads on every keystroke. */
export interface QueueLookups {
  readonly allWorkable: readonly QueueSummary[];
  /** Names for any id in the tree, including category queues. */
  readonly resolveQueueName: QueueNameResolver;
  readonly labelById: ReadonlyMap<string, string>;
  readonly personalQueueIds: ReadonlySet<string>;
}

export function useQueueLookups(queueTree: QueueTreeResponse | undefined): QueueLookups {
  const allWorkable = useMemo<readonly QueueSummary[]>(
    () => (queueTree === undefined ? [] : flattenWorkable(queueTree.roots)),
    [queueTree],
  );
  const resolveQueueName = useMemo(() => buildQueueNameResolver(queueTree), [queueTree]);
  const labelById = useMemo(
    () => new Map(allWorkable.map((q) => [q.id, displayNameFor(q)] as const)),
    [allWorkable],
  );
  const personalQueueIds = useMemo(
    () => new Set(allWorkable.filter((q) => q.isPersonal).map((q) => q.id)),
    [allWorkable],
  );
  return { allWorkable, resolveQueueName, labelById, personalQueueIds };
}
